import { useTranslation } from 'react-i18next';
import { CheckIcon } from '@heroicons/react/24/outline';

import { createHandle } from 'services/breadcrumbs/breadcrumbs';
import { useCurrentUser } from 'services/repositories/user/user';
import { useJoinWaitlist } from 'services/repositories/waitlist/waitlist';
import { NotificationType, useNotificationStore } from 'stores/notifications/notifications';
import { OrderFormsRoutes } from 'support/types';
import { Button } from 'components/Form/Button/Button';
import { Page } from 'components/Page/Page';

const ORDER_FORM_WAITLIST_KEY = 'ORDER_FORM';
export const OrderFormsPitch = () => {
  const { t } = useTranslation();
  const { data: currentUser } = useCurrentUser();
  const { addNotification } = useNotificationStore();
  const { mutate: joinWaitlist, isLoading } = useJoinWaitlist();

  const features = [
    t('orderForms:features.directDelivery'),
    t('orderForms:features.customerPricing'),
    t('orderForms:features.minimalOrderValues'),
    t('orderForms:features.brandedLogo'),
    t('orderForms:features.multiDevice'),
  ];

  const isOnWaitlist = Boolean(currentUser?.waitlists?.includes(ORDER_FORM_WAITLIST_KEY));

  const handleRequestFeature = () => {
    joinWaitlist(
      { key: ORDER_FORM_WAITLIST_KEY },
      {
        onSuccess: () => {
          addNotification(t('orderForms:waitlist.success.title'), NotificationType.success);
        },
        onError: () => {
          addNotification(
            {
              title: t('orderForms:waitlist.error.title'),
              subtitle: t('orderForms:waitlist.error.subtitle'),
            },
            NotificationType.error,
          );
        },
      },
    );
  };

  const handleLearnMore = () => {
    window.open(
      `${t('orderForms:learnMoreLink')}?utm_source=app&utm_medium=portal&utm_campaign=order_forms_pitch`,
      '_blank',
    );
  };

  return (
    <Page>
      {/* eslint-disable-next-line tailwindcss/no-contradicting-classname */}
      <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-bl from-[#E7FBF7] from-0% via-[#C9D3FC]/30 via-30% to-[#C9D3FC]/0 to-50%"></div>
      {/* Content positioned left-center like in Figma */}
      <div className="flex h-full justify-between gap-12 overflow-x-hidden">
        <div className="w-2/5 space-y-10 px-16 py-20">
          <div className="inline-flex items-center rounded-[10px] border border-violet-100 bg-violet-50 px-2 py-0.5">
            <span className="text-xs font-medium text-violet-800">{t('orderForms:badge')}</span>
          </div>

          <div className="space-y-3">
            <h1 className="text-2xl font-extrabold leading-8 text-gray-900">{t('orderForms:title')}</h1>
            <p className="text-sm leading-5 text-gray-700">{t('orderForms:subtitle')}</p>
          </div>

          <div className="space-y-2">
            {features.map((feature, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="shrink-0">
                  <CheckIcon className="size-5 text-emerald-400" aria-hidden="true" />
                </div>
                <p className="text-sm leading-5 text-gray-700">{feature}</p>
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              variant="primary"
              onClick={handleRequestFeature}
              loading={isLoading}
              disabled={isOnWaitlist}
              analyticsId="order-forms-join-waitlist"
            >
              {isOnWaitlist ? t('orderForms:waitlist.alreadyOnWaitlist') : t('orderForms:waitlist.join')}
            </Button>
            <Button type="button" variant="secondary" onClick={handleLearnMore} analyticsId="order-forms-learn-more">
              {t('orderForms:learnMore')}
            </Button>
          </div>
        </div>
        <div className="relative w-3/5">
          <img
            className="absolute right-0 top-28 w-[900px] -rotate-6 skew-x-12 rounded-lg shadow-[-3px_4px_0px_0px_rgba(201,211,252,1.00),-25px_42px_64px_0px_rgba(201,211,252,0.50),0px_12px_14px_0px_rgba(0,0,0,0.25)]"
            src={t('orderForms:imageUrl')}
          />
        </div>
      </div>
    </Page>
  );
};

OrderFormsPitch.handle = createHandle(() => {
  return {
    label: (t) => t('orderForms:title'),
    to: OrderFormsRoutes.pitch,
    key: OrderFormsRoutes.pitch,
  };
});
